// One admitted host and one server-issued lease own automatic capture. This
// controller deliberately never obtains new device permission on a timer.
const roles = new Set(['admin', 'super_admin', 'superadmin', 'church_admin', 'staff', 'faculty']);
const managerRoles = new Set(['admin', 'super_admin', 'superadmin', 'church_admin', 'staff']);
const timestamp = (value) => typeof value === 'number' ? value : Date.parse(value);
const cleanRole = (value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
export const createAutomaticRecordingAttemptId = (crypto) => {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto?.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('Recording needs a secure browser connection. Open this meeting using HTTPS.');
};
export const getAutomaticRecordingAttemptId = ({ crypto, storage, roomId, sessionId }) => {
  const key = `meeting-recording-attempt:${encodeURIComponent(roomId)}:${encodeURIComponent(sessionId)}`;
  try {
    const saved = storage?.getItem(key);
    if (typeof saved === 'string' && /^[a-zA-Z0-9_-]{24,128}$/.test(saved)) return saved;
  } catch { /* A private browser may disallow storage; a server lease still protects capture. */ }
  const attemptId = createAutomaticRecordingAttemptId(crypto);
  try { storage?.setItem(key, attemptId); } catch { /* Keep this attempt in the mounted controller. */ }
  return attemptId;
};
export const automaticRecordingConfigured = (room) =>
  typeof room?.settings?.automaticRecordingEnabled === 'boolean' || room?.settings?.autoRecord === true;
export const automaticRecordingEnabled = (room) =>
  room?.settings?.recordingEnabled !== false && (typeof room?.settings?.automaticRecordingEnabled === 'boolean'
    ? room.settings.automaticRecordingEnabled : room?.settings?.autoRecord === true);
export const canManageAutomaticRecording = (role) => managerRoles.has(cleanRole(role));
export const canAttemptAutomaticRecording = ({ room, role, participantStatus, sessionId }) =>
  Boolean(room?.roomId && sessionId && room?.access?.canRecord === true && roles.has(cleanRole(role)) &&
    ['admitted', 'presenting'].includes(String(participantStatus || '').toLowerCase()) && room?.isActive !== false &&
    !['ended', 'cancelled', 'deleted'].includes(String(room?.meetingStatus || room?.meetingSatatus || '').toLowerCase()));
export const recordingConfirmationSeconds = (dueAt, now = Date.now()) => {
  const deadline = typeof dueAt === 'number' ? dueAt : Date.parse(dueAt);
  return Number.isFinite(deadline) ? Math.max(0, Math.ceil((deadline - now) / 1000)) : 60;
};
export const describeRecordingFailure = (error) => {
  if (['NotAllowedError', 'SecurityError'].includes(error?.name) || error?.code === 'RECORDING_AUDIO_GESTURE_REQUIRED') {
    return 'Your browser needs permission or a click to activate recording audio. Select Confirm and start. Browser permission must be granted before recording can begin.';
  }
  return String(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Recording could not start. Confirm to try again.').slice(0, 600);
};

export class AutomaticRecordingController {
  constructor({ api, recorder, readContext, onChange, onStarted, now = Date.now, attemptId }) {
    Object.assign(this, { api, recorder, readContext, onChange, onStarted, now, attemptId });
    this.policy = null;
    this.busy = false;
    this.disposed = false;
    this.attempted = false;
    this.countdownAttempted = false;
    this.lastClaimAt = 0;
    this.error = '';
    this.manualStopped = false;
  }
  emit(extra = {}) {
    if (!this.disposed) this.onChange({ policy: this.policy, busy: this.busy, error: this.error, manualStopped: this.manualStopped, countdownAttempted: this.countdownAttempted, ...extra });
  }
  path(context = this.readContext()) { return `/recordings/${encodeURIComponent(context.room.roomId)}/automatic`; }
  isCurrent(context) {
    const current = this.readContext();
    return !this.disposed && !this.manualStopped && current.room?.roomId === context.room?.roomId &&
      current.sessionId === context.sessionId && canAttemptAutomaticRecording(current);
  }
  takePolicy(data) {
    this.policy = data?.automaticRecording || data?.data?.automaticRecording || null;
    this.emit();
    return this.policy;
  }
  async tick() {
    const context = this.readContext();
    if (this.disposed || this.manualStopped || this.busy || !canAttemptAutomaticRecording(context) || !automaticRecordingEnabled(context.room)) return;
    this.busy = true;
    try {
      const { data } = await this.api.get(this.path(context), { params: { sessionId: context.sessionId } });
      if (!this.isCurrent(context)) return;
      const policy = this.takePolicy(data);
      if (!policy?.enabled || ['recording', 'completed', 'skipped'].includes(policy.state)) {
        if (policy?.state === 'recording') this.onStarted?.();
        this.error = '';
        if (!policy?.enabled) { this.attempted = false; this.countdownAttempted = false; this.lastClaimAt = 0; }
        return;
      }
      if (policy.sessionId && policy.sessionId !== context.sessionId && policy.ownerPresent !== false && timestamp(policy.leaseUntil) > this.now()) return;
      if (policy.canCapture === false) return;
      if (!this.lastClaimAt || this.now() - this.lastClaimAt >= 20_000) {
        const claim = await this.api.post(`${this.path(context)}/claim`, { sessionId: context.sessionId, attemptId: this.attemptId });
        if (!this.isCurrent(context)) return;
        this.takePolicy(claim.data);
        if (claim.data?.claimed === false || this.policy?.enabled !== true || this.policy?.state === 'recording') return;
        this.lastClaimAt = this.now();
      }
      if (!this.attempted) {
        this.attempted = true;
        await this.capture(context);
      } else if (this.error && cleanRole(context.role) === 'faculty' && !this.countdownAttempted &&
        recordingConfirmationSeconds(this.policy?.confirmationDueAt, this.now()) === 0) {
        this.countdownAttempted = true;
        await this.capture(context);
      }
    } catch (error) {
      // A competing host owns the lease. A 409 is not evidence that recording
      // has started; only the server's recording state can establish that.
      if (error?.response?.status !== 409) this.error = describeRecordingFailure(error);
    } finally {
      this.busy = false;
      this.emit();
    }
  }
  async capture(context) {
    if (!this.isCurrent(context) || !this.policy?.enabled || this.policy.sessionId !== context.sessionId) return;
    this.error = '';
    this.emit({ busy: true });
    try {
      const result = await this.recorder.start({ roomId: context.room.roomId, title: context.room.title,
        sessionId: context.sessionId, automaticAttemptId: this.attemptId });
      if (!result?.recordingId) throw new Error('Recording did not start.');
      if (!this.isCurrent(context)) {
        await this.recorder.stopIfActive?.(context.room.roomId);
      } else {
        this.policy = { ...this.policy, state: 'recording' };
        this.onStarted?.();
      }
    } catch (error) {
      this.error = describeRecordingFailure(error);
      if (this.isCurrent(context)) {
        try {
          const { data } = await this.api.post(`${this.path(context)}/blocked`, {
            sessionId: context.sessionId, attemptId: this.attemptId, reason:
              error?.code === 'RECORDING_AUDIO_GESTURE_REQUIRED' ? 'audio-gesture' :
              ['NotAllowedError', 'SecurityError'].includes(error?.name) ? 'browser-permission' :
              error?.response?.status >= 500 ? 'storage-unavailable' : 'capture-unavailable',
          });
          if (this.isCurrent(context)) this.takePolicy(data);
        } catch { /* The next policy poll resolves an ended room or lost lease. */ }
      }
    }
    this.emit();
  }
  async confirm() {
    const context = this.readContext();
    if (this.busy || this.manualStopped || !canAttemptAutomaticRecording(context)) return;
    this.busy = true;
    try {
      await this.recorder.unlockRecordingAudio?.();
      const { data } = await this.api.post(`${this.path(context)}/claim`, { sessionId: context.sessionId, attemptId: this.attemptId });
      if (!this.isCurrent(context)) return;
      this.takePolicy(data);
      if (data?.claimed === false || this.policy?.enabled !== true) return;
      this.lastClaimAt = this.now();
      this.attempted = true;
      this.countdownAttempted = true;
      await this.capture(context);
    } catch (error) { this.error = describeRecordingFailure(error); }
    finally { this.recorder.releasePrimedAudio?.(); this.busy = false; this.emit(); }
  }
  async decision(decision) {
    const context = this.readContext();
    if (this.busy || !canAttemptAutomaticRecording(context) || !canManageAutomaticRecording(context.role)) return;
    this.busy = true;
    this.emit();
    try {
      await this.api.post(`/recordings/${encodeURIComponent(context.room.roomId)}/decision`, { decision, sessionId: context.sessionId });
      if (decision === 'record') this.manualStopped = false;
      if (!this.isCurrent(context)) return;
      this.error = '';
      this.attempted = false;
      this.countdownAttempted = false;
      this.lastClaimAt = 0;
      this.policy = { ...this.policy, enabled: decision === 'record', state: decision === 'record' ? 'pending' : 'skipped' };
    } catch (error) { this.error = describeRecordingFailure(error); }
    finally { this.busy = false; this.emit(); }
    if (decision === 'record') await this.tick();
  }
  captureFailed(message) {
    if (this.manualStopped) return;
    this.attempted = true;
    this.countdownAttempted = true;
    this.error = String(message || 'Capture stopped. Confirm to start again.').slice(0, 600);
    this.policy = { ...this.policy, state: 'blocked' };
    this.emit();
  }
  captureStopped() {
    this.manualStopped = true;
    this.attempted = true;
    this.countdownAttempted = true;
    this.error = '';
    this.policy = { ...this.policy, state: 'stopping' };
    this.emit();
  }
  stopSettled({ success, message } = {}) {
    if (!this.manualStopped) return;
    this.policy = { ...this.policy, state: success ? 'completed' : 'paused' };
    this.error = success ? '' : String(message || 'Recording save needs a retry.').slice(0, 500);
    this.emit();
  }
  dispose() { this.disposed = true; }
}
